import { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar } from 'lucide-react';
import type { GoalSummaryData } from '@/types/goals';
import { cn } from '@/lib/utils';
import {
  SLIDE_PADDING,
  SLIDE_CARD,
  SLIDE_SCROLL_BOTTOM_PAD,
  STORY_CARD_BG,
  NARRATIVE,
} from './constants';
import { StoryCardHeader } from './StoryCardHeader';
import { Counter } from './StoryCounter';

export function CardReadingDays({
  summary,
  active,
  className,
}: Readonly<{
  summary: GoalSummaryData;
  active: boolean;
  className?: string;
}>) {
  const pagesByDay = useMemo(() => {
    const map = new Map<string, number>();
    for (const d of summary.daily_pages) {
      map.set(d.date, d.pages_read);
    }
    const start = new Date(summary.goal.start_date + 'T00:00:00');
    const end = new Date(summary.goal.end_date + 'T00:00:00');
    const out: Array<{ date: string; pages: number }> = [];
    const cur = new Date(start);
    while (cur <= end) {
      const dateStr = cur.toISOString().split('T')[0];
      out.push({ date: dateStr, pages: map.get(dateStr) ?? 0 });
      cur.setDate(cur.getDate() + 1);
    }
    const maxPages = Math.max(1, ...out.map((d) => d.pages));
    
    // Add intensity level to each day
    return out.map((d) => {
      // For the contribution graph we want a 5-step scale
      let level = 0;
      if (d.pages > 0) {
        const ratio = d.pages / maxPages;
        level = Math.max(1, Math.ceil(ratio * 5)); // 1 to 5
      }
      return {
        ...d,
        level,
      };
    });
  }, [summary.daily_pages, summary.goal.start_date, summary.goal.end_date]);

  return (
    <Card
      className={cn(SLIDE_CARD, 'relative overflow-hidden', STORY_CARD_BG, className)}
    >
      <CardContent
        className={cn(
          'flex min-h-0 flex-1 flex-col justify-start gap-5 overflow-y-auto overscroll-y-contain py-4 sm:gap-6',
          SLIDE_PADDING,
          SLIDE_SCROLL_BOTTOM_PAD
        )}
      >
        <StoryCardHeader
          icon={<Calendar className="h-5 w-5" />}
          title="วันที่อ่าน"
          subtitle="ความสม่ำเสมอในแต่ละวัน"
          narrative={NARRATIVE.readingDays}
        />

        <div className="rounded-2xl border border-primary/20 bg-background/30 p-5 shadow-[inset_0_2px_8px_hsl(var(--foreground)/0.03)] mix-blend-multiply dark:mix-blend-normal">
          <div className="flex flex-wrap items-baseline gap-2 gap-y-1">
            <span className="text-4xl font-bold tabular-nums text-primary sm:text-5xl">
              <Counter value={summary.active_days} active={active} />
            </span>
            <span className="text-lg text-muted-foreground">
              / {summary.total_days_in_goal} วัน
            </span>
          </div>
          <div className="mt-4 flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5">
              <div className="h-2.5 w-2.5 rounded-full bg-primary" />
              <span className="text-muted-foreground">
                อ่าน{' '}
                <Counter value={summary.active_days} active={active} /> วัน
              </span>
            </div>
            <div className="flex items-center gap-2 rounded-full bg-muted/60 px-3 py-1.5 border border-border/40">
              <div className="h-2.5 w-2.5 rounded-full bg-muted-foreground/40" />
              <span className="text-muted-foreground">
                ไม่อ่าน{' '}
                <Counter value={summary.missed_days} active={active} /> วัน
              </span>
            </div>
          </div>
        </div>

        <div className="flex w-full flex-1 flex-col overflow-hidden min-h-0">
          <p className="mb-2 shrink-0 text-xs font-semibold text-foreground sm:text-sm">
            จำนวนหน้าต่อวัน
          </p>
          <div className="flex min-h-0 flex-1 flex-wrap content-start gap-2 overflow-y-auto">
            {pagesByDay.map(({ date, pages, level }) => (
              <div
                key={date}
                className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-sm text-sm font-bold tabular-nums sm:h-11 sm:w-11 sm:text-base',
                  level === 0 && 'bg-muted/40 text-muted-foreground/60 dark:bg-muted/30',
                  level === 1 && 'bg-primary/20 text-primary',
                  level === 2 && 'bg-primary/40 text-primary',
                  level === 3 && 'bg-primary/50 text-primary-foreground',
                  level === 4 && 'bg-primary/80 text-primary-foreground',
                  level === 5 && 'bg-primary text-primary-foreground shadow-sm'
                )}
              >
                {pages}
              </div>
            ))}
          </div>

          {/* Legend */}
          <div className="mt-4 shrink-0 flex items-center justify-end gap-2 text-xs text-muted-foreground sm:text-sm">
            <span>น้อย</span>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((level) => (
                <div
                  key={level}
                  className={cn(
                    'h-3 w-3 rounded-[2px] sm:h-4 sm:w-4',
                    level === 1 && 'bg-primary/20',
                    level === 2 && 'bg-primary/40',
                    level === 3 && 'bg-primary/50',
                    level === 4 && 'bg-primary/80',
                    level === 5 && 'bg-primary'
                  )}
                />
              ))}
            </div>
            <span>มาก</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
